import { useState, useEffect } from 'react'
import _ from 'lodash'

import { Box, Checkbox, Heading, Stack } from '~/design_system'
import AddressSubForm from './AddressSubForm'
import fetch from '~/common/fetch'
import { getAddressFields } from '~/components/v2/Address/addressUtils'

const HQ = 'hq'
const MAILING = 'mailing'

function _parseRequiredFields(requirements, countryCode) {
  const transformedRequirements = { country_code: true }
  if (!requirements || !(countryCode in requirements)) {
    return transformedRequirements
  }
  requirements[countryCode].forEach(requirement => {
    if (requirement.field === 'postal_code') {
      transformedRequirements.zip = requirement.required
    } else if (requirement.field === 'subdivision') {
      transformedRequirements.state = requirement.required
    } else {
      transformedRequirements[requirement.field] = requirement.required
    }
  })
  return transformedRequirements
}

const UncontrolledCompanyAddressForm = ({
  hqInitialAddress,
  mailingInitialAddress,
  isSame,
  formResources,
  hqRequired,
  mailingRequired,
  onAddressChange,
  hqAddressErrors,
  mailingAddressErrors
}) => {
  const [hqAddress, setHQAddress] = useState(hqInitialAddress || {})
  const [mailingAddress, setMailingAddress] = useState(mailingInitialAddress || {})
  const [hqRequiredFields, setHQRequiredFields] = useState(hqRequired, {})
  const [mailingRequiredFields, setMailingRequiredFields] = useState(mailingRequired, {})
  const [hqSubdivisionsList, setHQSubdivisionList] = useState([])
  const [mailingSubdivisionsList, setMailingSubdivisionList] = useState([])
  const [isSameAddressChecked, setIsSameAddressChecked] = useState(isSame || false)
  const [hqErrors, setHQErrors] = useState(hqAddressErrors || {})
  const [mailingErrors, setMailingErrors] = useState(mailingAddressErrors || {})
  const _updateSubdivisionsList = async (country, addressType) => {
    const results = await fetch.get(`/api/v2/country-subdivisions/?country__alpha_3_code=${country}&no_page=true`)
    const options = results.map(s => ({
      value: s.code,
      label: s.subdivision_name
    }))
    const requiredCopy = addressType === HQ ? { ...hqRequiredFields } : { ...mailingRequiredFields }

    if (results.length === 0) {
      delete requiredCopy.state
    } else {
      requiredCopy.state = true
    }

    if (addressType === HQ) {
      setHQSubdivisionList(options)
    } else {
      setMailingSubdivisionList(options)
    }

    return requiredCopy
  }
  const _getRequiredFieldsForCountry = async countryCode => {
    return _parseRequiredFields(await getAddressFields(countryCode), countryCode)
  }

  const _setRequiredFields = async (countryCode, AddressType) => {
    if (AddressType === HQ) {
      setHQRequiredFields(await _getRequiredFieldsForCountry(countryCode))
    } else if (AddressType === MAILING) {
      setMailingRequiredFields(await _getRequiredFieldsForCountry(countryCode))
    }
  }
  const _checkFieldErrors = (values, requiredFields) => {
    const newErrors = {}
    Object.keys(values).forEach(field => {
      if (requiredFields[field] && (values[field] === null || values[field] === '')) {
        newErrors[field] = true
      }
    })

    return newErrors
  }

  const _onHQAddressChange = async values => {
    const copiedValues = { ...hqAddress, ...values }

    // update subdivision list
    let updatedRequired = hqRequiredFields
    if (hqAddress.country_code !== copiedValues.country_code) {
      await _updateSubdivisionsList(values.country_code, HQ)
      updatedRequired = await _getRequiredFieldsForCountry(values.country_code)
      setHQRequiredFields(updatedRequired)
      if (isSameAddressChecked) {
        await _updateSubdivisionsList(copiedValues.country_code, MAILING)
        setMailingRequiredFields(updatedRequired)
        const updatedMailingErrors = _checkFieldErrors(copiedValues, updatedRequired)
        setMailingErrors({ updatedMailingErrors })
      }
    }

    // 3. Save data
    setHQAddress(copiedValues)
  }

  const _onHQFieldBlur = () => {
    // 1. Check and update checkbox or update mailing component to be the same
    const isSame = _compareAddress(hqAddress, mailingAddress)
    if (isSame) {
      setIsSameAddressChecked(true)
    } else if (isSameAddressChecked) {
      const updatedMailingData = { ...hqAddress, id: mailingAddress.id, address_type: mailingAddress.address_type }
      setMailingAddress(updatedMailingData)
      onAddressChange({
        values: updatedMailingData,
        required: mailingRequiredFields,
        addressType: MAILING
      })
    }

    // 2. update address to be submitted
    onAddressChange({
      values: hqAddress,
      required: hqRequiredFields,
      addressType: HQ
    })

    // 3. Update required fields msg
    const updatedHQErrors = _checkFieldErrors(hqAddress, hqRequiredFields)
    const omittedErrors = _.omit(hqErrors, Object.keys(hqAddress))
    setHQErrors({ ...omittedErrors, ...updatedHQErrors })
    if (isSameAddressChecked) {
      setMailingErrors({ ...omittedErrors, ...updatedHQErrors })
    }
  }

  useEffect(() => {
    if (hqInitialAddress) {
      setHQAddress(hqInitialAddress)
      _setRequiredFields(hqInitialAddress.country_code, HQ)
    }
    if (mailingInitialAddress) {
      setMailingAddress(mailingInitialAddress)
      _setRequiredFields(hqInitialAddress.country_code, MAILING)
    }
  }, [hqInitialAddress, mailingInitialAddress])

  const _onMailingAddressChange = async values => {
    const copiedValues = { ...mailingAddress, ...values }

    // 1. update subdivision list
    let updatedRequired = mailingRequiredFields
    if (mailingAddress.country_code !== copiedValues.country_code) {
      updatedRequired = await _updateSubdivisionsList(copiedValues.country_code, MAILING)
      updatedRequired = await _getRequiredFieldsForCountry(values.country_code)
      setMailingRequiredFields(updatedRequired)
    }

    // 2. clean up data and save
    setMailingAddress(copiedValues)
  }

  const _onMailingFieldBlur = () => {
    const isSame = _compareAddress(hqAddress, mailingAddress)
    setIsSameAddressChecked(isSame)
    onAddressChange({
      values: mailingAddress,
      required: mailingRequiredFields,
      addressType: MAILING
    })

    // Update required field msg
    const updatedMailingErrors = _checkFieldErrors(mailingAddress, mailingRequiredFields)
    const omittedErrors = _.omit(mailingErrors, Object.keys(mailingAddress))
    setMailingErrors({ ...omittedErrors, ...updatedMailingErrors })
  }

  const _compareAddress = (source, toCompare) => {
    const addressCompareExcludeFields = ['id', 'address_type']
    const fields = Object.keys(source).filter(f => !addressCompareExcludeFields.includes(f))
    return fields.every(field => source[field] === toCompare[field])
  }

  const setSameAddressChecked = async checked => {
    if (checked) {
      await _updateSubdivisionsList(hqAddress.country_code)
      const updatedAddress = { ...hqAddress, id: mailingAddress.id, address_type: mailingAddress.address_type }
      setMailingAddress(updatedAddress)
      onAddressChange({
        values: updatedAddress,
        required: hqRequiredFields,
        addressType: MAILING
      })
    }
    setIsSameAddressChecked(checked)
  }

  useEffect(() => {
    let formattedMailingSubList = formResources?.mailingSubdivisionsList || []
    formattedMailingSubList = formattedMailingSubList.map(s => ({
      value: s.code,
      label: s.subdivision_name
    }))
    let formattedHQSubList = formResources?.hqSubdivisionsList || []
    formattedHQSubList = formattedHQSubList.map(s => ({
      value: s.code,
      label: s.subdivision_name
    }))

    setMailingSubdivisionList(formattedMailingSubList)
    setHQSubdivisionList(formattedHQSubList)
  }, [formResources])

  useEffect(() => {
    if (!_.isEmpty(hqAddress) && !_.isEmpty(mailingAddress)) {
      const isSame = _compareAddress(hqAddress, mailingAddress)
      if (isSame) {
        setIsSameAddressChecked(true)
      }
    }
    if (!_.isEmpty(hqAddress)) {
      const initHQErrors = _checkFieldErrors(hqAddress, hqRequiredFields)
      setHQErrors({ ...initHQErrors, ...hqErrors })
    }

    if (!_.isEmpty(mailingAddress)) {
      const initMailingErrors = _checkFieldErrors(mailingAddress, mailingRequiredFields)
      setMailingErrors({ ...initMailingErrors, ...mailingErrors })
    }
  }, [hqAddress, mailingAddress])

  useEffect(() => {
    if (hqAddressErrors) {
      setHQErrors(hqAddressErrors)
    }
    if (mailingAddressErrors) {
      setMailingErrors(mailingAddressErrors)
    }
  }, [hqAddressErrors, mailingAddressErrors])

  return (
    <Stack>
      <Heading size="md">Headquarters Address*</Heading>
      <Box data-testid="hq_address">
        <AddressSubForm
          id="hq_address"
          values={hqAddress}
          errors={hqErrors}
          countriesList={formResources.countriesList}
          subdivisionsList={hqSubdivisionsList}
          requiredFields={hqRequiredFields}
          onChange={_onHQAddressChange}
          onBlur={_onHQFieldBlur}
          onCountryBlur={_onHQFieldBlur}
          useExtendedAddressRequirements
        />
      </Box>
      <Heading size="md">Mailing Address*</Heading>
      <Checkbox isChecked={isSameAddressChecked} onChange={e => setSameAddressChecked(e.target.checked)}>
        Same as Headquarters Address
      </Checkbox>
      <Box data-testid="mailing_address">
        <AddressSubForm
          id="mailing_address"
          values={mailingAddress}
          errors={mailingErrors}
          countriesList={formResources.countriesList}
          subdivisionsList={mailingSubdivisionsList}
          requiredFields={mailingRequiredFields}
          onChange={_onMailingAddressChange}
          onBlur={_onMailingFieldBlur}
          onCountryBlur={_onMailingFieldBlur}
          useExtendedAddressRequirements
        />
      </Box>
    </Stack>
  )
}

export default UncontrolledCompanyAddressForm
